import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { Link, useNavigate, useLocation } from 'react-router-dom'

import { styled } from '@mui/material/styles'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import CssBaseline from '@mui/material/CssBaseline'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Paper from '@mui/material/Paper'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'

import {
  AdminIcon,
  AppMenuIcon,
  CloseIcon,
  CourseIcon,
  CreatorIcon,
  LiveClassIcon,
  MenuIcon,
  ManagerIcon,
  NotificationIcon,
  ProfileIcon,
  ScribeIcon,
  EventIcon,
} from 'src/v3/core/icons'
import {
  fetchProfileImage,
  selectProfileImage,
  selectProfileImageStatus,
} from 'src/v3/self-paced/self-paced.slice'
import {
  fetchNotifications,
  selectNotifications,
  selectNoficationsStatus,
  selectUseEvents,
  selectUseCreateV4,
} from 'src/v3/settings/settings.slice'
import { useAuth0 } from '@auth0/auth0-react'
import axios from 'src/utilities/http-client'
import { selectBoundTreeStatus, selectRoles } from '../settings/settings.slice'

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  backgroundColor: theme.palette.background.default,
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.background.default,
  borderBottom: '1px solid white',
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    backgroundColor: theme.palette.background.default,
    width: '100%',
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}))

type SubMenuItem = {
  name: string
  route: string
  icon?: JSX.Element
  disabled?: boolean
  subMenu?: boolean
  divider?: boolean
  allowInLimited?: boolean
}

type AppMenuItem = {
  name: string
  route: string
  icon: JSX.Element
  role?: string
  testid?: string
}

interface Props {
  children: ReactElement | ReactElement[]
  subMenuItems: SubMenuItem[]
  domain: string
  role?: string
}

export default ({
  children,
  subMenuItems,
  domain,
  role,
}: Props): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { pathname } = useLocation()
  let { logout } = useAuth0()

  let boundTreeStatus = useAppSelector(selectBoundTreeStatus)
  let roles = useAppSelector(selectRoles)

  let profileImage = useAppSelector(selectProfileImage)
  let profileImageStatus = useAppSelector(selectProfileImageStatus)
  let useEvents = useAppSelector(selectUseEvents)
  let notifications = useAppSelector(selectNotifications)
  let notificationsStatus = useAppSelector(selectNoficationsStatus)

  let [subMenuOpen, setSubMenuOpen] = useState<boolean>(false)
  let [appMenuOpen, setAppMenuOpen] = useState<boolean>(false)
  let [notificationsOpen, setNotificationsOpen] = useState<boolean>(false)
  let [notificationDelete, setNotificationDelete] = useState<string>('')
  let useCreateV4 = useAppSelector(selectUseCreateV4)

  let unreadNotifications = notifications.filter(
    (n) => n.status === 'unread',
  ).length
  let isBoundtree =
    boundTreeStatus === 'subscribed' || boundTreeStatus === 'limited'

  useEffect(() => {
    if (profileImageStatus === 'uninitialized') {
      dispatch(fetchProfileImage())
    }
  }, [profileImageStatus])

  useEffect(() => {
    if (notificationsStatus === 'uninitialized') {
      dispatch(fetchNotifications())
    }
  }, [notificationsStatus])

  let appMenuItems: AppMenuItem[] = [
    {
      name: 'Profile',
      route: '/profile',
      icon: <ProfileIcon />,
      testid: 'right-menu-profile',
    },
    {
      name: 'Self-Paced',
      route: '/self-paced',
      icon: <CourseIcon />,
      testid: 'right-menu-self-paced',
    },
    {
      name: 'Live Classes',
      route: '/live',
      icon: <LiveClassIcon />,
      testid: 'right-menu-live',
    },
    {
      name: 'Events',
      route: '/events',
      icon: <EventIcon />,
      testid: 'right-menu-events',
    },
    {
      name: 'Admin',
      route: '/admin',
      icon: <AdminIcon />,
      role: 'admin',
      testid: 'right-menu-admin',
    },
    {
      name: 'Manage',
      route: '/manage',
      icon: <ManagerIcon />,
      role: 'manager',
      testid: 'right-menu-manage',
    },
    {
      name: 'Create',
      route: '/create',
      icon: <CreatorIcon />,
      role: 'creator',
      testid: 'right-menu-create',
    },
    {
      name: 'Create v4',
      route: '/create/v4',
      icon: <CreatorIcon />,
      role: 'creator',
      testid: 'right-menu-create-v4',
    },
    {
      name: 'Scribe',
      route: '/scribe',
      icon: <ScribeIcon />,
      role: 'scribe',
      testid: 'right-menu-scribe',
    },
  ]

  return role && !roles?.includes(role) ? (
    <></>
  ) : (
    <Box sx={{ display: 'flex', background: 'background.default' }}>
      <CssBaseline />
      <AppBar
        sx={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', border: 'none' }}
        position="fixed"
      >
        <Toolbar sx={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <IconButton
            data-testid="left-menu-toggle"
            color="inherit"
            aria-label="open left sub menu"
            onClick={() => {
              setSubMenuOpen(!subMenuOpen)
              setAppMenuOpen(false)
            }}
            edge="start"
            sx={{ mr: 5 }}
          >
            <MenuIcon />
          </IconButton>
          <div style={{ width: '100%', display: 'flex' }}>
            <div
              style={{ width: subMenuOpen ? 70 : 0, transition: 'width .3s' }}
            ></div>
            <div style={{ height: 50 }}>
              <div style={{ cursor: 'pointer', marginTop: -16 }}>
                <Link to="/self-paced">
                  <img
                    data-testid={isBoundtree ? 'boundtree-logo' : 'logo'}
                    className="pb-2 mt-4"
                    src={isBoundtree ? '/btlogo.png' : '/logo.svg'}
                    alt="logo"
                    width={isBoundtree ? '360px' : '120px'}
                    height="130px"
                  />
                </Link>
              </div>
            </div>
          </div>
          {boundTreeStatus !== 'limited' && (
            <Button
              onClick={() => navigate('/search')}
              sx={{
                width: 100,
                color: pathname.includes('search') ? '#42a5f5' : '#fff',
                display: 'block',
              }}
            >
              Search
            </Button>
          )}
          <>
            <IconButton
              sx={{
                position: 'relative',
                color: unreadNotifications > 0 ? 'primary.main' : 'white',
              }}
              onClick={() => setNotificationsOpen(!notificationsOpen)}
            >
              <NotificationIcon />
              {unreadNotifications > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    fontSize: 12,
                    top: 1,
                    right: 0,
                    textAlign: 'center',
                    width: 14,
                  }}
                >
                  {unreadNotifications}
                </Box>
              )}
            </IconButton>
            {notificationsOpen && (
              <ClickAwayListener
                onClickAway={() => setNotificationsOpen(false)}
              >
                <List
                  sx={{
                    position: 'absolute',
                    top: '64px',
                    right: 8,
                    py: 0,
                    overflow: 'scroll',
                    maxWidth: 400,
                  }}
                >
                  {notifications.length > 0 ? (
                    notifications.map((n) => (
                      <ListItem
                        key={n.id}
                        sx={{
                          py: 1.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 0.75,
                          bgcolor: 'background.paper',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: '#333333',
                            borderLeftColor:
                              n.status === 'unread'
                                ? 'primary.main'
                                : '#333333',
                          },
                          borderLeftWidth: 6,
                          borderLeftStyle: 'solid',
                          borderLeftColor:
                            n.status === 'unread'
                              ? 'primary.dark'
                              : 'background.paper',
                        }}
                        onClick={() => {
                          setNotificationsOpen(false)
                          axios
                            .put('/api/settings/notification/status', {
                              id: n.id,
                              status: 'read',
                            })
                            .then(() => dispatch(fetchNotifications()))
                          navigate(n.route)
                        }}
                      >
                        <Typography>{n.message}</Typography>
                        <Box
                          sx={{
                            mr: -1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: 34,
                            width: 34,
                          }}
                        >
                          {notificationDelete === n.id ? (
                            <CircularProgress size={24} sx={{ mx: '5px' }} />
                          ) : (
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation()
                                setNotificationDelete(n.id)
                                axios
                                  .delete(
                                    `/api/settings/notification?notificationID=${n.id}`,
                                  )
                                  .then(() => dispatch(fetchNotifications()))
                                  .finally(() => setNotificationDelete(''))
                              }}
                            >
                              <CloseIcon />
                            </IconButton>
                          )}
                        </Box>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem
                      sx={{
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: '#333333',
                        },
                      }}
                      onClick={() => setNotificationsOpen(false)}
                    >
                      You have no notifications.
                    </ListItem>
                  )}
                </List>
              </ClickAwayListener>
            )}
          </>
          <Button
            data-testid="right-menu-toggle"
            aria-label="open right sub menu"
            onClick={() => {
              setAppMenuOpen(!appMenuOpen)
              setSubMenuOpen(false)
            }}
            sx={{ ml: 1, color: 'inherit' }}
          >
            <AppMenuIcon />
            <Avatar
              src={profileImage}
              sx={{ height: 30, width: 30, marginLeft: 1 }}
            />
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor="left"
        open={subMenuOpen}
        onClose={() => setSubMenuOpen(false)}
        PaperProps={{
          elevation: 2,
          sx: { bgcolor: 'background.default', p: 1, backgroundImage: 'none' },
        }}
      >
        <List sx={{ mt: '58px', minWidth: 300 }}>
          {subMenuItems
            .filter(
              (item) => boundTreeStatus !== 'limited' || item.allowInLimited,
            )
            .map((item) => (
              <ListItem
                key={item.name}
                disablePadding
                sx={{ display: 'block', ml: item.subMenu ? 1 : 0 }}
              >
                {item.divider && <Divider sx={{ borderColor: 'gray' }} />}
                <ListItemButton
                  onClick={() => navigate(`/${domain}${item.route}`)}
                  disabled={item.disabled}
                >
                  {item.icon && (
                    <ListItemIcon
                      sx={{ minWidth: 0, mr: 3, justifyContent: 'center' }}
                    >
                      {item.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText primary={item.name} sx={{ mr: 1 }} />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Drawer>
      <Drawer
        anchor="right"
        open={appMenuOpen}
        onClose={() => setAppMenuOpen(false)}
        PaperProps={{
          elevation: 2,
          sx: { bgcolor: 'background.default', p: 1, backgroundImage: 'none' },
        }}
      >
        <List sx={{ mt: '58px', minWidth: 300 }}>
          <ListItem
            disablePadding
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              my: 1,
            }}
          >
            <Avatar src={profileImage} sx={{ height: 100, width: 100 }} />
            <Paper
              elevation={2}
              sx={{ bgcolor: 'background.default', mt: -1.5, zIndex: 2 }}
            >
              <Button
                size="small"
                sx={{ px: 1 }}
                onClick={() =>
                  logout({
                    logoutParams: {
                      returnTo: import.meta.env.VITE_AUTH0_LOGOUT_REDIRECT_URI,
                    },
                  })
                }
              >
                Logout
              </Button>
            </Paper>
          </ListItem>
          <Divider sx={{ borderColor: 'gray' }} />
          {appMenuItems
            .filter(
              (item) =>
                item.route !== `/${domain}` &&
                (!item.role || roles?.includes(item.role)) &&
                !(
                  item.name === 'Live Classes' && boundTreeStatus === 'limited'
                ) &&
                !(item.name === 'Events' && !useEvents) &&
                !(item.name === 'Create v4' && !useCreateV4),
            )
            .map((item) => (
              <ListItem
                data-testid={item.testid ?? null}
                key={item.name}
                disablePadding
                sx={{ display: 'block' }}
              >
                <ListItemButton onClick={() => navigate(item.route)}>
                  <ListItemIcon
                    sx={{ minWidth: 0, mr: 3, justifyContent: 'center' }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.name} sx={{ mr: 1 }} />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      </Drawer>
      <div className="w-full">
        <DrawerHeader />
        {domain !== 'self-paced' &&
          domain !== 'scribe' &&
          domain !== 'create-v4' &&
          domain !== 'create/v4' && (
            <div
              style={{
                minWidth: '95%',
                background: `url(/${domain}-banner.png)`,
                minHeight: '200px',
                backgroundPosition: '0px 48%',
                backgroundSize: 'cover',
              }}
            ></div>
          )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: domain !== 'self-paced' && domain !== 'live' ? 3 : 0,
            mt:
              domain !== 'self-paced' && domain !== 'live'
                ? 0
                : {
                    xs: 4,
                    md: 0,
                  },
          }}
        >
          {children}
        </Box>
      </div>
    </Box>
  )
}
